import { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Search, ChevronDown, Check } from "lucide-react";
import { COUNTRIES } from "../lib/countries";

/**
 * Phone field with country code dropdown.
 * value: full E.164-ish string e.g. "+31 612345678"
 * onChange(value): emits combined string
 * default country: NL
 */
export default function PhoneField({ value = "", onChange, defaultCountry = "NL", testId, className = "" }) {
  // Initial parse: if value starts with +, find matching dial.
  const initial = useMemo(() => {
    if (value && value.startsWith("+")) {
      const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
      const match = sorted.find((c) => value.replace(/^\+/, "").startsWith(c.dial));
      if (match) {
        const rest = value.replace(/^\+/, "").slice(match.dial.length).trim();
        return { country: match, number: rest };
      }
    }
    return { country: COUNTRIES.find((c) => c.code === defaultCountry) || COUNTRIES[0], number: value };
  }, []); // eslint-disable-line

  const [country, setCountry] = useState(initial.country);
  const [number, setNumber] = useState(initial.number);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const emit = (c, n) => {
    const cleaned = n.replace(/[^\d\s]/g, "").trim();
    onChange?.(cleaned ? `+${c.dial} ${cleaned}` : "");
  };

  const setNum = (n) => { setNumber(n); emit(country, n); };
  const setC = (c) => { setCountry(c); setOpen(false); setQ(""); emit(c, number); };

  const filtered = useMemo(() => {
    if (!q) return COUNTRIES;
    const s = q.toLowerCase();
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(s) || c.dial.includes(s) || c.code.toLowerCase().includes(s));
  }, [q]);

  return (
    <div className={`flex items-stretch border border-black/20 bg-white ${className}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId ? `${testId}-country` : undefined}
            className="flex items-center gap-1.5 px-3 py-2 border-r border-black/15 hover:bg-black/5 transition-colors min-w-[100px] text-sm"
          >
            <span className="text-base leading-none">{country.flag}</span>
            <span className="font-medium">+{country.dial}</span>
            <ChevronDown size={12} className="text-zinc-500 ml-auto"/>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="bg-white border-black/15 rounded-none p-0 w-72">
          <div className="border-b border-black/10 p-2 flex items-center gap-2">
            <Search size={14} className="text-zinc-500"/>
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search country…"
              className="flex-1 text-sm bg-transparent focus:outline-none"
            />
          </div>
          <ul className="max-h-72 overflow-y-auto">
            {filtered.map((c) => (
              <li key={c.code}>
                <button
                  type="button"
                  onClick={() => setC(c)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-black/5 ${c.code === country.code ? "bg-black/5" : ""}`}
                >
                  <span className="text-base">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-zinc-500">+{c.dial}</span>
                  {c.code === country.code && <Check size={12} className="text-black"/>}
                </button>
              </li>
            ))}
            {filtered.length === 0 && <li className="px-3 py-4 text-sm text-zinc-500 text-center">No matches</li>}
          </ul>
        </PopoverContent>
      </Popover>
      <input
        data-testid={testId}
        type="tel"
        value={number}
        onChange={(e) => setNum(e.target.value)}
        placeholder="Phone number"
        className="flex-1 px-3 py-2 bg-transparent text-sm focus:outline-none"
      />
    </div>
  );
}
